import { useState } from "react";
import { Calendar, X } from "lucide-react";
import { Modal } from "../../ui/modal";

interface PayLinkFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: { status: string; fromDate: string; toDate: string }) => void;
}

export const PayLinkFilterModal: React.FC<PayLinkFilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
}) => {
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const handleApplyFilters = () => {
    onApply({ status, fromDate, toDate });
    onClose();
  };

  const resetFilters = () => {
    setStatus("");
    setFromDate("");
    setToDate("");
    onApply({ status: "", fromDate: "", toDate: "" });
    onClose();
  };

  const statusOptions = ["ALL", "PAID", "PENDING", "FAILED", "CANCELLED", "EXPIRED", "DECLINED"];

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-md mx-auto" showCloseButton={false} transparency={0.5}>
      <div className="bg-white rounded-lg shadow-xl w-full dark:bg-gray-900">

        {/* Header */}
        <div className="flex justify-between items-center border-b p-4 dark:border-gray-700">
          <h2 className="text-xl font-semibold dark:text-white">Filter Payment Links</h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">

          {/* Date Range */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date Range</h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <label className="text-sm text-gray-500">From Date</label>
                <div
                  className="flex items-center border rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 cursor-pointer"
                  onClick={() => (document.getElementById("payLinkFromDate") as HTMLInputElement)?.showPicker()}
                >
                  <span className="pl-3 pr-2 text-gray-400 flex-shrink-0">
                    <Calendar size={16} />
                  </span>
                  <input
                    id="payLinkFromDate"
                    type="date"
                    className="flex-1 py-2 pr-2 text-sm text-gray-700 dark:text-white bg-transparent outline-none border-none cursor-pointer"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex-1">
                <label className="text-sm text-gray-500">To Date</label>
                <div
                  className="flex items-center border rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 cursor-pointer"
                  onClick={() => (document.getElementById("payLinkToDate") as HTMLInputElement)?.showPicker()}
                >
                  <span className="pl-3 pr-2 text-gray-400 flex-shrink-0">
                    <Calendar size={16} />
                  </span>
                  <input
                    id="payLinkToDate"
                    type="date"
                    className="flex-1 py-2 pr-2 text-sm text-gray-700 dark:text-white bg-transparent outline-none border-none cursor-pointer"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Status Pills */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</h3>
            <div className="flex flex-wrap gap-2">
              {statusOptions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s === "ALL" ? "" : s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    (s === "ALL" && status === "") || status.toUpperCase() === s
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="border-t dark:border-gray-700 p-4 flex justify-end space-x-3">
          <button
            type="button"
            onClick={resetFilters}
            className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-md hover:bg-gray-50"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleApplyFilters}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            Apply Filters
          </button>
        </div>

      </div>
    </Modal>
  );
};